'use client';

import {
  createTheme,
  CSSVariablesResolver,
  MantineColorsTuple,
  Modal,
  rem,
  Tooltip,
  v8CssVariablesResolver,
} from '@mantine/core';
import modalOverrides from './themeOverrides/modal.module.css';
import tooltipOverrides from './themeOverrides/tooltip.module.css';

const stone: MantineColorsTuple = [
  '#FAFAF9', // 0 stone-50
  '#F5F5F4', // 1 stone-100
  '#E7E5E4', // 2 stone-200
  '#D6D3D1', // 3 stone-300
  '#A8A29E', // 4 stone-400
  '#78716C', // 5 stone-500
  '#57534E', // 6 stone-600
  '#3C3835', // 7 stone-700
  '#292524', // 8 stone-800
  '#1C1917', // 9 stone-900
];

const gem: MantineColorsTuple = [
  '#F5F0FE', // 0 gem-50
  '#E8DCFA', // 1 gem-100
  '#C2A8EC', // 2 gem-200
  '#9B72D8', // 3 gem-300
  '#7B4EC0', // 4 gem-400
  '#6236A0', // 5 gem-500
  '#4E2A8E', // 6 gem-600
  '#3B1F6E', // 7 gem-700
  '#2A1650', // 8 gem-800 (interpolated)
  '#1A0D35', // 9 gem-900 (interpolated)
];

const rose: MantineColorsTuple = [
  '#FEF4F8', // 0 rose-50
  '#FAE0EC', // 1 rose-100
  '#EFBFCC', // 2 rose-200 (interpolated)
  '#E47EAA', // 3 rose-300
  '#D4568E', // 4 rose-400
  '#C03A7E', // 5 rose-500
  '#A8215C', // 6 rose-600
  '#881344', // 7 rose-700
  '#640D32', // 8 rose-800 (interpolated)
  '#440820', // 9 rose-900 (interpolated)
];

const citrine: MantineColorsTuple = [
  '#FFFAED', // 0 cit-50
  '#FCF1CC', // 1 cit-100
  '#F4D88E', // 2 cit-200 (interpolated)
  '#ECC05A', // 3 cit-300
  '#E0A820', // 4 cit-400
  '#C48C00', // 5 cit-500
  '#A07000', // 6 cit-600
  '#7A5600', // 7 cit-700 (interpolated)
  '#553C00', // 8 cit-800 (interpolated)
  '#302200', // 9 cit-900 (interpolated)
];

export const theme = createTheme({
  primaryColor: 'rose',
  primaryShade: { light: 5, dark: 4 },
  // Mantine 9 changed the default radius (sm -> md) and `variant="light"`
  // colors (transparent tints -> solid mixes); pin the old defaultRadius
  // and layer v8CssVariablesResolver below to keep this theme's existing
  // look after the v8 -> v9 upgrade.
  defaultRadius: 'sm',
  fontFamily: "'Outfit', system-ui, sans-serif",
  headings: { fontFamily: "'Outfit', system-ui, sans-serif" },
  spacing: {
    none: rem(0),
    '5xs': rem(1),
    '4xs': rem(2),
    '3xs': rem(4),
    '2xs': rem(6),
    xs: rem(8),
    sm: rem(12),
    md: rem(16),
    lg: rem(20),
    xl: rem(24),
    '2xl': rem(32),
    '3xl': rem(48),
    '4xl': rem(64),
    '5xl': rem(96),
    '6xl': rem(128),
    '7xl': rem(192),
    '8xl': rem(256),
  },
  fontSizes: {
    '2xs': rem(9),
    xs: rem(13),
    sm: rem(14),
    md: rem(16),
    lg: rem(20),
    xl: rem(24),
    '2xl': rem(28),
    '3xl': rem(32),
    '4xl': rem(40),
    '7xl': rem(80),
  },
  colors: {
    stone,
    gem,
    rose,
    citrine,
  },
  components: {
    Modal: Modal.extend({
      classNames: modalOverrides,
    }),
    Tooltip: Tooltip.extend({
      classNames: tooltipOverrides,
    }),
  },
});

export const cssVariablesResolver: CSSVariablesResolver = (theme) => {
  const v8 = v8CssVariablesResolver(theme);

  return {
    variables: {
      ...v8.variables,
      // stone-950 sits outside the 10-slot tuple; needed for dark mode page bg
      '--stone-950': '#110F0E',
      // Status
      '--ok': '#22C55E',
      '--ok-muted': '#16A34A',
      '--ok-bg': '#DCFCE7',
      '--err': '#EF4444',
      '--err-muted': '#DC2626',
      '--err-bg': '#FEE2E2',
      '--warn': '#F59E0B',
      '--info': '#60A5FA',
      // Social platforms
      '--ig': '#E1306C',
      '--fb': '#1877F2',
      '--tt': '#2A2A2A',
      '--li': '#0A66C2',
      '--yt': '#FF0000',
      '--tw': '#1DA1F2',
      '--pin': '#E60023',
      // Calendar "today" indicator — no palette match; light and dark share the same hue
      '--theme-color-today': '#d95b42',
      '--theme-color-today-text': '#FFFFFF',
      // Motion
      '--ease-spring': 'cubic-bezier(0.16, 1, 0.3, 1)',
      '--ease-smooth': 'cubic-bezier(0.4, 0, 0.2, 1)',
      '--dur-fast': '120ms',
      '--dur-normal': '240ms',
      '--dur-slow': '420ms',
    },
    light: {
      ...v8.light,
      '--mantine-color-text': theme.colors.stone[7],
      '--mantine-color-body': theme.colors.stone[1],
      // text
      '--theme-text-primary-default': theme.colors.stone[9],
      '--theme-text-primary-hover': theme.colors.stone[9],
      '--theme-text-primary-active': theme.colors.stone[8],
      '--theme-text-primary-disabled': theme.colors.stone[4],
      '--theme-text-secondary-default': theme.colors.stone[5],
      '--theme-text-secondary-hover': theme.colors.stone[7],
      '--theme-text-secondary-active': theme.colors.stone[6],
      '--theme-text-secondary-disabled': theme.colors.stone[4],
      // icon
      '--theme-icon-primary-default': theme.colors.stone[7],
      '--theme-icon-primary-hover': theme.colors.stone[9],
      '--theme-icon-primary-active': theme.colors.stone[8],
      '--theme-icon-primary-disabled': theme.colors.stone[3],
      '--theme-icon-secondary-default': theme.colors.stone[5],
      '--theme-icon-secondary-hover': theme.colors.stone[7],
      '--theme-icon-secondary-active': theme.colors.stone[6],
      '--theme-icon-secondary-disabled': theme.colors.stone[3],
      // background
      '--theme-background-primary-default': '#FFFFFF',
      '--theme-background-primary-hover': theme.colors.stone[1],
      '--theme-background-primary-active': theme.colors.stone[2],
      '--theme-background-secondary-default': theme.colors.stone[1],
      '--theme-background-secondary-hover': theme.colors.stone[2],
      '--theme-background-secondary-active': theme.colors.stone[2],
      // border
      '--theme-border-primary-default': theme.colors.stone[2],
      '--theme-border-primary-hover': theme.colors.stone[3],
      '--theme-border-primary-disabled': theme.colors.stone[2],
      '--theme-border-secondary-default': theme.colors.stone[2],
      '--theme-border-secondary-hover': theme.colors.stone[3],
      '--theme-border-secondary-disabled': theme.colors.stone[2],
      '--theme-border-critical-default': theme.colors.red[4],
      '--theme-border-critical-hover': theme.colors.red[8],
      // layout: modal/tooltip surfaces = bg-elevated (stone-0 = white)
      '--theme-color-layout-background': '#FFFFFF',
      '--theme-color-content-outline': theme.colors.stone[2],
    },
    dark: {
      ...v8.dark,
      '--mantine-color-text': theme.colors.stone[0],
      '--mantine-color-body': '#110F0E',
      // text
      '--theme-text-primary-default': theme.colors.stone[1],
      '--theme-text-primary-hover': theme.colors.stone[0],
      '--theme-text-primary-active': theme.colors.stone[1],
      '--theme-text-primary-disabled': theme.colors.stone[5],
      '--theme-text-secondary-default': theme.colors.stone[4],
      '--theme-text-secondary-hover': theme.colors.stone[2],
      '--theme-text-secondary-active': theme.colors.stone[3],
      '--theme-text-secondary-disabled': theme.colors.stone[5],
      // icon
      '--theme-icon-primary-default': theme.colors.stone[1],
      '--theme-icon-primary-hover': theme.colors.stone[0],
      '--theme-icon-primary-active': theme.colors.stone[1],
      '--theme-icon-primary-disabled': theme.colors.stone[6],
      '--theme-icon-secondary-default': theme.colors.stone[4],
      '--theme-icon-secondary-hover': theme.colors.stone[2],
      '--theme-icon-secondary-active': theme.colors.stone[3],
      '--theme-icon-secondary-disabled': theme.colors.stone[6],
      // background
      '--theme-background-primary-default': theme.colors.stone[8],
      '--theme-background-primary-hover': theme.colors.stone[7],
      '--theme-background-primary-active': theme.colors.stone[7],
      '--theme-background-secondary-default': theme.colors.stone[9],
      '--theme-background-secondary-hover': theme.colors.stone[8],
      '--theme-background-secondary-active': theme.colors.stone[8],
      // border
      '--theme-border-primary-default': 'rgba(255,255,255,0.08)',
      '--theme-border-primary-hover': 'rgba(255,255,255,0.14)',
      '--theme-border-primary-disabled': 'rgba(255,255,255,0.05)',
      '--theme-border-secondary-default': 'rgba(255,255,255,0.05)',
      '--theme-border-secondary-hover': 'rgba(255,255,255,0.08)',
      '--theme-border-secondary-disabled': 'rgba(255,255,255,0.05)',
      '--theme-border-critical-default': theme.colors.red[4],
      '--theme-border-critical-hover': theme.colors.red[8],
      // layout: modal/tooltip surfaces = bg-elevated (stone-800)
      '--theme-color-layout-background': theme.colors.stone[8],
      '--theme-color-content-outline': 'rgba(255,255,255,0.08)',
    },
  };
};
